import { useCallback, useMemo, useState } from 'react';

import { ExamStepEnum } from 'src/services/offline/db';
import { getOrderIDB } from 'src/services/offline/outboxClient';
import {
  APP_EVENTS,
  captureAppEvent,
  TABLE_CONTEXT,
} from 'src/services/posthogEvents';

import { SearchPatientFormValues } from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { Api } from 'src/models/Api.model';
import { DEFAULT_PAGE_SIZE } from 'src/models/DefaultPageSize.model';
import {
  CriteriaGroupGroupElementsInner,
  CriteriaGroupGroupElementsInnerOperatorEnum,
} from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { CriteriaGroupXGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupXGroupElementsInner.model';
import {
  DeviceDataView,
  ExamData,
} from 'src/models/GalenData/DeviceDataView.model';
import { EntityCriteriaOperatorEnum } from 'src/models/GalenData/EntityCriteria.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { PageOfDeviceDataView } from 'src/models/GalenData/PageOfDeviceDataView.model';
import { PageOfUser } from 'src/models/GalenData/PageOfUser.model';
import { SortOrderEnum } from 'src/models/GalenData/SortOrder.model';

import { getPracticeUserEmailsByOrderId } from 'src/utils/getPracticeUserEmail';
import { isNetworkError } from 'src/utils/isNetworkError';
import {
  getPatientFilterConditions,
  sanitizeSearchPatientValues,
} from 'src/utils/patientFilters';

import useHandleHttpRequestError from './useHandleHttpRequestError';
export type OrderWithExam = {
  id: string;
  order: DeviceDataView<OrderData>;
  exam: DeviceDataView<ExamData> | undefined;
  lastStep?: ExamStepEnum;
  isResumable: boolean;
};

export const useGetFilterOrder = () => {
  const [ordersData, setOrdersData] =
    useState<PageOfDeviceDataView<OrderData>>();
  const [ordersWithExams, setOrdersWithExams] = useState<OrderWithExam[]>();
  const [userEmailsByOrderId, setUserEmailsByOrderId] = useState<
    Record<string, string>
  >({});
  const [isGettingOrders, setIsGettingOrders] = useState(false);
  const [hasNetworkFailure, setHasNetworkFailure] = useState(false);

  const { showHttpRequestError } = useHandleHttpRequestError();

  const getSortingParams = useCallback((status: OrderStatusEnum[]) => {
    return status.includes(OrderStatusEnum.REVIEW)
      ? {
        sortBy: ['data.Priority.value', 'data.ReportDatetime.value'],
        sortOrder: SortOrderEnum.ASC,
      }
      : {
        sortBy: ['maxValueProvidedOn'],
        sortOrder: SortOrderEnum.DESC,
      };
  }, []);

  const fetchOrdersCore = useCallback(
    async ({
      status,
      pageNumber,
      pageSize,
      siteId,
      patientsData,
    }: {
      status: OrderStatusEnum[];
      pageNumber: number;
      pageSize: number;
      siteId?: string;
      patientsData?: PageOfUser;
    }): Promise<PageOfDeviceDataView<OrderData> | undefined> => {
      const { sortBy, sortOrder } = getSortingParams(status);

      if (patientsData?.totalElements === 0) {
        const emptyResult: PageOfDeviceDataView<OrderData> = {
          content: [],
          totalElements: 0,
          totalPages: 0,
        };
        setOrdersData(emptyResult);
        setOrdersWithExams(undefined);
        return emptyResult;
      }

      const groupElements: CriteriaGroupGroupElementsInner[] = [
        {
          groupElements: status.map((status) => ({
            key: 'Status',
            value: status,
            operator: CriteriaGroupXGroupElementsInnerOperatorEnum.Equal,
          })),
          isOr: true,
        },
        ...(siteId
          ? [
            {
              key: 'SiteId',
              value: siteId,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            },
          ]
          : []),
      ];

      const ordersPage = await Api.remote.get_exam_order({
        groupElements,
        ownerFilter: {
          users: patientsData?.content?.map((patient) => patient.userId),
        },
        params: {
          pageNumber,
          pageSize,
          sortBy,
          sortOrder,
        },
      });

      const result = {
        content: ordersPage.data.content,
        totalElements: ordersPage.data.totalElements,
        totalPages: ordersPage.data.totalPages,
      };

      setOrdersData(result);
      setOrdersWithExams(undefined);
      return result;
    },
    [getSortingParams],
  );

  const queryFilteredOrders = useCallback(
    async ({
      siteId,
      pageNumber,
      searchValues,
      status,
      pageSize,
    }: {
      siteId: string | undefined;
      pageNumber: number;
      searchValues?: SearchPatientFormValues;
      status: OrderStatusEnum[];
      pageSize: number;
    }): Promise<PageOfDeviceDataView<OrderData> | undefined> => {
      const sanitizedSearchValues = sanitizeSearchPatientValues(searchValues);

      if (sanitizedSearchValues.length === 0) {
        return await fetchOrdersCore({
          status,
          pageNumber,
          pageSize,
          siteId,
        });
      }

      const patientFiltersWithSiteId = [
        ...getPatientFilterConditions(sanitizedSearchValues),
        ...(siteId
          ? [
            {
              key: import.meta.env.VITE_USER_SITES_FIELD_ID,
              value: `\\"${siteId}\\"`,
              operator: EntityCriteriaOperatorEnum.Like,
              isCustomField: true,
              isDataValue: false,
            },
          ]
          : []),
      ];

      const patientsData = await Api.remote._get_users_advanced(
        patientFiltersWithSiteId,
        {
          pageNumber: 0,
          sortBy: ['createdOn'],
          sortOrder: SortOrderEnum.DESC,
        },
      );

      return await fetchOrdersCore({
        status,
        pageNumber,
        pageSize,
        siteId,
        patientsData: patientsData.data,
      });
    },
    [fetchOrdersCore],
  );

  const getFilteredOrdersCore = useCallback(
    async (
      args: Parameters<typeof queryFilteredOrders>[0],
    ): Promise<PageOfDeviceDataView<OrderData> | undefined> => {
      try {
        const result = await queryFilteredOrders(args);
        setHasNetworkFailure(false);
        return result;
      } catch (error) {
        if (isNetworkError(error)) {
          setHasNetworkFailure(true);
        }
        throw error;
      }
    },
    [queryFilteredOrders],
  );

  const getFilteredOrders = useCallback(
    async ({
      siteId,
      pageNumber,
      searchValues,
      status = [OrderStatusEnum.OPEN],
      pageSize = DEFAULT_PAGE_SIZE,
    }: {
      siteId: string | undefined;
      pageNumber: number;
      searchValues?: SearchPatientFormValues;
      status?: OrderStatusEnum[];
      pageSize?: number;
    }) => {
      try {
        setIsGettingOrders(true);
        const orders = await getFilteredOrdersCore({
          siteId,
          pageNumber,
          pageSize,
          searchValues,
          status,
        });
        const filterFlags = {
          table: TABLE_CONTEXT.OPEN_ORDERS,
          has_first_name: Boolean(searchValues?.firstName?.trim()),
          has_last_name: Boolean(searchValues?.lastName?.trim()),
          has_id: Boolean(searchValues?.id?.trim()),
          has_dob: Boolean(searchValues?.dob),
          has_site_filter: Boolean(siteId),
        };
        const content = orders?.content ?? [];
        if (content.length === 0) {
          setOrdersWithExams([]);
          setUserEmailsByOrderId({});
          captureAppEvent(APP_EVENTS.ORDERS_LOADED, {
            count: 0,
            page_number: pageNumber + 1,
            total_pages: orders?.totalPages,
            total_elements: orders?.totalElements,
            ...filterFlags,
          });
          return [];
        }
        const isOpenOrdersList = status.every(
          (orderStatus) => orderStatus === OrderStatusEnum.OPEN,
        );
        const examByOrderId = new Map<string, DeviceDataView<ExamData>>();
        if (!isOpenOrdersList) {
          const orderIds = content.map(({ data }) => data.OrderId.value);
          const examResponse = await Api.remote.get_exam_practice_user_from_order_id({
            orderIds,
          });
          (examResponse.data.content ?? []).forEach((exam) => {
            examByOrderId.set(exam.data.OrderId.value, exam);
          });
        }
        const merged = await Promise.all(
          content.map(async (order) => {
            const orderId = order.data.OrderId.value;
            const idbOrder = await getOrderIDB(orderId);
            return {
              id: orderId,
              order,
              exam: examByOrderId.get(orderId),
              lastStep: idbOrder?.LastStep,
              isResumable: idbOrder?.LastStep !== undefined,
            };
          }),
        );
        setOrdersWithExams(merged);
        setUserEmailsByOrderId(await getPracticeUserEmailsByOrderId(merged));
        captureAppEvent(APP_EVENTS.ORDERS_LOADED, {
          count: merged.length,
          page_number: pageNumber + 1,
          total_pages: orders?.totalPages,
          total_elements: orders?.totalElements,
          ...filterFlags,
        });
        return merged;
      } catch (error) {
        showHttpRequestError(error);
      } finally {
        setIsGettingOrders(false);
      }
    },
    [getFilteredOrdersCore, showHttpRequestError],
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [searchValues, setSearchValues] = useState<SearchPatientFormValues>();

  const handleSetCurrentPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSetSearchValues = useCallback(
    (values: SearchPatientFormValues | undefined) => {
      setSearchValues(values);
    },
    [],
  );

  const dataResource = useMemo(
    () => ({
      content: ordersData?.content ?? [],
      totalElements: ordersData?.totalElements ?? 0,
      totalPages: ordersData?.totalPages ?? 0,
    }),
    [ordersData],
  );

  const handleLoadOrders = useCallback(
    async (
      pageNumber: number,
      pageSize: number,
      siteId: string | undefined,
      status: OrderStatusEnum[],
      searchValues?: SearchPatientFormValues,
    ) => {
      const result = await getFilteredOrders({
        pageNumber,
        pageSize,
        siteId,
        status,
        searchValues,
      });
      if (result !== undefined) {
        setCurrentPage(pageNumber + 1);
      }
    },
    [getFilteredOrders],
  );

  return {
    getFilteredOrders,
    getFilteredOrdersCore,
    ordersData,
    ordersWithExams,
    userEmailsByOrderId,
    // Rows fetched before the connection dropped cannot be shown as current, so
    // consumers keep their loading state until a fetch succeeds again.
    isGettingOrders: isGettingOrders || hasNetworkFailure,
    currentPage,
    searchValues,
    handleSetCurrentPage,
    handleSetSearchValues,
    dataResource,
    handleLoadOrders,
  };
};
